import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface CarePlanData {
  id: string;
  plan_name: string;
  location_label: string;
  location_state: string;
  latitude: number;
  longitude: number;
  search_radius_miles: number;
  consolidation_level: number;
  insurance_status: string | null;
  plan_year: number;
  family_member_name: string | null;
  status: string;
  error_message: string | null;
  optimized_total_cost: number | null;
  national_avg_total_cost: number | null;
  savings_vs_national: number | null;
  savings_percentage: number | null;
  total_procedures: number;
  total_hospitals_used: number;
  share_token: string;
  is_public: boolean;
  created_at: string;
  updated_at: string;
}

export interface CarePlanScenario {
  id: string;
  care_plan_id: string;
  scenario_id: string;
  planned_start_date: string | null;
  optimized_scenario_cost: number | null;
  national_avg_scenario_cost: number | null;
  sort_order: number;
  scenario_name?: string;
  scenario_slug?: string;
  scenario_description?: string;
}

export interface CarePlanAssignment {
  id: string;
  care_plan_id: string;
  care_plan_scenario_id: string;
  code: string;
  code_type: string;
  description: string | null;
  phase: string | null;
  quantity: number;
  day_number: number | null;
  setting: string | null;
  assigned_hospital_id: string | null;
  assigned_hospital_name: string | null;
  assigned_hospital_city: string | null;
  assigned_hospital_state: string | null;
  distance_miles: number | null;
  hospital_unit_price: number | null;
  hospital_total_price: number | null;
  national_avg_price: number | null;
  national_total_price: number | null;
  savings: number | null;
  markup_percentage: number | null;
  is_blended: boolean;
  assignment_reason: string | null;
  sort_order: number;
}

interface UseCarePlanResult {
  plan: CarePlanData | null;
  scenarios: CarePlanScenario[];
  assignments: CarePlanAssignment[];
  isLoading: boolean;
  error: string | null;
  isAuthorized: boolean;
}

export function useCarePlan(planId: string | undefined, shareToken: string | null): UseCarePlanResult {
  // Fetch care plan — auto-poll every 3s while pending/computing
  const { data: plan, isLoading: planLoading, error: planError } = useQuery({
    queryKey: ['care-plan', planId],
    queryFn: async () => {
      if (!planId) return null;
      const { data, error } = await supabase
        .from('care_plans')
        // Explicit columns: user_email / stripe_session_id / anonymous_user_id
        // are column-revoked from public roles (PII) — select('*') would 403.
        .select('id, plan_name, latitude, longitude, location_label, location_state, search_radius_miles, insurance_status, plan_year, family_member_name, share_token, is_public, status, error_message, optimized_total_cost, national_avg_total_cost, savings_vs_national, savings_percentage, total_procedures, total_hospitals_used, created_at, updated_at, consolidation_level')
        .eq('id', planId)
        .single();
      if (error) throw error;
      return data as CarePlanData;
    },
    enabled: !!planId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return (status === 'pending' || status === 'computing') ? 3000 : false;
    },
  });

  // Check authorization: public plan OR valid share token
  const isAuthorized = !plan ? false : (
    plan.is_public || plan.share_token === shareToken
  );

  // Fetch scenarios with joined scenario info
  const { data: scenarios, isLoading: scenariosLoading } = useQuery({
    queryKey: ['care-plan-scenarios', planId],
    queryFn: async () => {
      if (!planId) return [];
      const { data, error } = await supabase
        .from('care_plan_scenarios')
        .select('*')
        .eq('care_plan_id', planId)
        .order('sort_order');
      if (error) throw error;

      // Fetch scenario names separately
      if (data && data.length > 0) {
        const scenarioIds = data.map(s => s.scenario_id);
        const { data: scenarioInfo } = await supabase
          .from('scenarios')
          .select('id, name, slug, description')
          .in('id', scenarioIds);

        const infoMap = new Map(scenarioInfo?.map(s => [s.id, s]) || []);
        return data.map(s => ({
          ...s,
          scenario_name: infoMap.get(s.scenario_id)?.name,
          scenario_slug: infoMap.get(s.scenario_id)?.slug,
          scenario_description: infoMap.get(s.scenario_id)?.description,
        })) as CarePlanScenario[];
      }
      return data as CarePlanScenario[];
    },
    enabled: !!planId && isAuthorized,
  });

  // Fetch procedure assignments
  const { data: assignments, isLoading: assignmentsLoading } = useQuery({
    queryKey: ['care-plan-assignments', planId],
    queryFn: async () => {
      if (!planId) return [];
      const { data, error } = await supabase
        .from('care_plan_procedure_assignments')
        .select('*')
        .eq('care_plan_id', planId)
        .order('sort_order');
      if (error) throw error;
      return data as CarePlanAssignment[];
    },
    enabled: !!planId && isAuthorized,
  });

  return {
    plan: plan || null,
    scenarios: scenarios || [],
    assignments: assignments || [],
    isLoading: planLoading || scenariosLoading || assignmentsLoading,
    error: planError ? (planError as Error).message : null,
    isAuthorized,
  };
}
